import React, { useCallback } from "react";

import StarIcon from "@mui/icons-material/Star";
import type { AutocompleteRenderInputParams, ToggleButtonProps } from "@mui/material";
import { Autocomplete, Grid, TextField, ToggleButton, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";

import type { DropdownOption } from "@waterly/frontend-components";
import { isOptionEqualToValue, getOptionLabel } from "@waterly/frontend-components";

export type SystemsToolbarProps = {
  onChangeStateFilter: (event: React.SyntheticEvent<Element, Event>, value: DropdownOption) => void;
  stateFilterOptions: DropdownOption[];
  stateFilterValue: DropdownOption;
  filterFavorites: boolean;
  onChangeFilterFavorites: ToggleButtonProps["onChange"];
};

const toggleButtonSx = { border: "none" };
const toolBarSx = { pb: 2, width: "100%" };
const starSx = { ml: 0.5 };

const SystemsToolbar = ({
  onChangeStateFilter,
  stateFilterOptions,
  stateFilterValue,
  filterFavorites,
  onChangeFilterFavorites,
}: SystemsToolbarProps) => {
  const { t } = useTranslation(["common"]);

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField {...params} label={t("common:select_state")} fullWidth />
    ),
    [t],
  );

  return (
    <Grid container alignItems="center" justifyContent="space-between" sx={toolBarSx}>
      <Grid
        size={{
          xs: 6,
          sm: 5,
          md: 4,
        }}
      >
        <Autocomplete
          isOptionEqualToValue={isOptionEqualToValue}
          onChange={onChangeStateFilter}
          options={stateFilterOptions}
          value={stateFilterValue}
          disabled={filterFavorites}
          disableClearable
          fullWidth
          getOptionLabel={getOptionLabel}
          renderInput={renderInput}
        />
      </Grid>
      <Grid>
        <Tooltip
          placement="top"
          title={filterFavorites ? t("common:show_all") : t("common:show_only_favorites")}
        >
          <ToggleButton
            value="filterFavorites"
            selected={filterFavorites}
            onChange={onChangeFilterFavorites}
            sx={toggleButtonSx}
            color="secondary"
          >
            {t("common:favorites")}
            <StarIcon fontSize="small" sx={starSx} />
          </ToggleButton>
        </Tooltip>
      </Grid>
    </Grid>
  );
};

export default SystemsToolbar;
